"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { FiEdit2, FiTrash2, FiEye, FiPlus, FiSearch, FiChevronDown, FiAlertTriangle, FiXCircle } from "react-icons/fi"
import { toast } from "react-toastify"
import { api } from "../../services/api"
import { motion, AnimatePresence } from "framer-motion"

const Tickets = () => {
  const [tickets, setTickets] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" })
  const [currentTicket, setCurrentTicket] = useState({
    type: "reclamation",
    subject: "",
    description: "",
    status: "nouveau",
    reservation_id: "",
  })
  const [editing, setEditing] = useState(false)
  const [reservations, setReservations] = useState([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [ticketToDelete, setTicketToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [activeTab, setActiveTab] = useState("tous")
  const navigate = useNavigate()

  // Gestion du flou de l'arrière-plan de la page
  useEffect(() => {
    const pageContainer = document.getElementById('page-container');
    if (!pageContainer) return;

    const isAnyModalOpen = isOpen || showDeleteDialog;

    if (isAnyModalOpen) {
      // Empêcher le défilement de la page de fond
      document.body.style.overflow = 'hidden';
      // Appliquer l'assombrissement uniquement à l'overlay
      const overlays = document.querySelectorAll('.modal-overlay');
      overlays.forEach(overlay => {
        overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      });
    } else {
      // Rétablir le défilement normal
      document.body.style.overflow = 'auto';
    }

    // Nettoyage au démontage du composant
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, showDeleteDialog]);

  // Empêcher la propagation des clics sur le contenu de la modale
  const handleModalClick = (e) => {
    e.stopPropagation();
  };


  // Fonction de tri des tickets
  const sortTickets = (items) => {
    if (!sortConfig.key) return items

    return [...items].sort((a, b) => {
      if (!a[sortConfig.key] && !b[sortConfig.key]) return 0
      if (!a[sortConfig.key]) return sortConfig.direction === "asc" ? -1 : 1
      if (!b[sortConfig.key]) return sortConfig.direction === "asc" ? 1 : -1

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1
      }
      return 0
    })
  }

  // Composant pour afficher le badge de statut
  const StatusBadge = ({ status }) => {
    const statusMap = {
      nouveau: {
        label: "Nouveau",
        className: "bg-amber-500/10 text-amber-400 border border-amber-500/20 shadow-sm",
        icon: "🆕",
      },
      en_cours: {
        label: "En cours",
        className: "bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-sm",
        icon: "⏳",
      },
      resolu: {
        label: "Résolu",
        className: "bg-green-500/10 text-green-400 border border-green-500/20 shadow-sm",
        icon: "✅",
      },
      ferme: {
        label: "Fermé",
        className: "bg-gray-600/20 text-gray-400 border border-gray-600/30 shadow-sm",
        icon: "🔒",
      },
    }

    const statusInfo = statusMap[status] || {
      label: status,
      className: "bg-gray-600/20 text-gray-400 border border-gray-600/30 shadow-sm",
      icon: "ℹ️",
    }

    return (
      <div
        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${statusInfo.className} transition-all duration-200 hover:scale-105`}
      >
        <span className="mr-1.5">{statusInfo.icon}</span>
        {statusInfo.label}
      </div>
    )
  }

  const getStatusLabel = (status) => {
    const labels = {
      nouveau: "Nouveau",
      en_cours: "En cours",
      resolu: "Résolu",
      ferme: "Fermé",
    }
    return labels[status] || status
  }

  const fetchTickets = useCallback(async () => {
    try {
      setIsLoading(true)
      setError("")
      const [ticketsResponse, reservationsResponse] = await Promise.all([api.getClientTickets(), api.getClientQuotes()])
      const ticketsData = Array.isArray(ticketsResponse) ? ticketsResponse : []
      const reservationsData = reservationsResponse?.data || []
      setTickets(ticketsData)
      setReservations(reservationsData)
    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error)
      setError("Impossible de charger les données. Veuillez réessayer plus tard.")
      toast.error("Erreur lors du chargement des données")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTickets()
  }, [fetchTickets])

  const openDialog = (ticket = null) => {
    if (ticket) {
      setCurrentTicket({
        ...ticket,
        reservation_id: ticket.reservation_id || "",
      })
      setEditing(true)
    } else {
      setCurrentTicket({
        type: "reclamation",
        subject: "",
        description: "",
        status: "nouveau",
        reservation_id: "",
      })
      setEditing(false)
    }
    setError("")
    setIsOpen(true)
  }

  const closeDialog = () => {
    setIsOpen(false)
    setError("")
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setCurrentTicket((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setError("")
      setIsSubmitting(true)

      // Vérifier que le type est valide (reclamation ou remboursement)
      const validType = ['reclamation', 'remboursement'].includes(currentTicket.type) 
        ? currentTicket.type 
        : 'reclamation';

      const ticketData = {
        type: validType,
        subject: currentTicket.subject,
        description: currentTicket.description,
        // Ne pas envoyer le statut pour la création, il sera défini par défaut côté serveur
      }

      // Le serveur attend soit quote_id, soit reservation_id
      if (currentTicket.reservation_id) {
        // Si c'est un nombre, c'est probablement un ID de devis/quote
        if (!isNaN(currentTicket.reservation_id)) {
          ticketData.quote_id = currentTicket.reservation_id;
        } else {
          // Sinon, on l'envoie tel quel et le serveur vérifiera s'il s'agit d'une réservation valide
          ticketData.reservation_id = currentTicket.reservation_id;
        }
      }

      if (editing) {
        await api.updateTicket(currentTicket.id, ticketData)
        toast.success("Ticket mis à jour avec succès")
      } else {
        await api.createTicket(ticketData)
        toast.success("Ticket créé avec succès")
      }
      await fetchTickets()
      closeDialog()
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Une erreur est survenue lors de la soumission du ticket"
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const confirmDelete = (ticketId) => {
    setTicketToDelete(ticketId)
    setShowDeleteDialog(true)
  }

  const handleDelete = async () => {
    if (!ticketToDelete) return
    try {
      setIsDeleting(true)
      setError("")
      await api.deleteTicket(ticketToDelete)
      await fetchTickets()
      toast.success("Ticket supprimé avec succès")
    } catch (error) {
      const errorMessage = error.message || "Une erreur est survenue lors de la suppression du ticket"
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
      setTicketToDelete(null)
    }
  }

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false)
    setTicketToDelete(null)
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    try {
      const options = { year: "numeric", month: "long", day: "numeric" }
      return new Date(dateString).toLocaleDateString("fr-FR", options)
    } catch (error) {
      return "Date invalide"
    }
  }

  const filteredTickets = sortTickets(
    tickets.filter((ticket) => {
      const matchesSearch =
        searchTerm === "" ||
        Object.values(ticket).some(
          (value) => value && value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
        )
      const matchesTab = activeTab === "tous" || ticket.status === activeTab
      return matchesSearch && matchesTab
    }),
  )

  return (
    <div id="page-container" className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="mb-6 md:mb-0">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
              Gestion des tickets
            </h1>
            <p className="mt-2 text-gray-400 text-lg">Gérez vos demandes et réclamations en toute simplicité</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02, boxShadow: "0 10px 25px rgba(245, 158, 11, 0.2)" }}
            whileTap={{ scale: 0.98 }}
            onClick={() => openDialog()}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-amber-500/25 transition-all duration-200 border border-amber-500/20"
          >
            <FiPlus className="mr-2 h-5 w-5" />
            Nouveau ticket
          </motion.button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-gray-800/50 border border-amber-500/20 rounded-2xl shadow-xl backdrop-blur-sm mb-8 overflow-hidden">
          <div className="p-6 border-b border-amber-500/10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-amber-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-4 py-3 border border-amber-500/30 rounded-xl bg-gray-800/50 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all duration-200"
                  placeholder="Rechercher un ticket..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <select
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value)}
                    className="appearance-none bg-gray-800/50 border border-amber-500/30 text-gray-200 rounded-xl px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all duration-200"
                  >
                    <option value="tous" className="bg-gray-800">Tous les statuts</option>
                    <option value="nouveau" className="bg-gray-800">Nouveau</option>
                    <option value="en_cours" className="bg-gray-800">En cours</option>
                    <option value="resolu" className="bg-gray-800">Résolu</option>
                    <option value="ferme" className="bg-gray-800">Fermé</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-amber-400">
                    <FiChevronDown className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {["tous", "nouveau", "en_cours", "resolu", "ferme"].map((tab) => (
                <motion.button
                  key={tab}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                    activeTab === tab
                      ? "bg-amber-500/20 text-amber-400 border border-amber-500/30 shadow-lg"
                      : "text-gray-400 hover:bg-gray-700/50 border border-gray-700/50 hover:text-gray-300"
                  }`}
                >
                  {tab === "tous" ? "Tous les tickets" : getStatusLabel(tab)}
                </motion.button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
                    <div className="h-6 w-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gray-400 text-lg">Chargement des tickets...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6 mb-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FiAlertTriangle className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-red-300 font-medium">{error}</p>
                  </div>
                </div>
              </div>
            ) : filteredTickets.length > 0 ? (
              <div className="space-y-4">
                {filteredTickets.map((ticket) => (
                  <motion.div
                    key={ticket.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-800/30 border border-amber-500/10 rounded-xl p-6 hover:bg-gray-800/50 hover:border-amber-500/20 transition-all duration-200 group"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex-1 min-w-0 mb-4 lg:mb-0">
                        <div className="flex items-center flex-wrap gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-white group-hover:text-amber-400 transition-colors duration-200">
                            {ticket.subject}
                          </h3>
                          <StatusBadge status={ticket.status} />
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed line-clamp-2 mb-3">{ticket.description}</p>
                        <div className="flex items-center text-sm text-gray-500">
                          <svg className="mr-2 h-4 w-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                          {formatDate(ticket.created_at)}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => openDialog(ticket)} className="p-3 rounded-lg text-gray-400 hover:text-amber-400 hover:bg-amber-500/10" title="Modifier">
                          <FiEdit2 className="h-5 w-5" />
                        </motion.button>
                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => confirmDelete(ticket.id)} className="p-3 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10" title="Supprimer">
                          <FiTrash2 className="h-5 w-5" />
                        </motion.button>
                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => navigate(`/client/tickets/${ticket.id}`)} className="p-3 rounded-lg text-gray-400 hover:text-green-400 hover:bg-green-500/10" title="Voir les détails">
                          <FiEye className="h-5 w-5" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 px-4">
                <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
                  <svg className="h-12 w-12 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {searchTerm ? "Aucun résultat trouvé" : "Aucun ticket pour le moment"}
                </h3>
                <p className="text-gray-400 max-w-md mx-auto mb-6">
                  {searchTerm ? "Aucun ticket ne correspond à votre recherche." : "Créez votre premier ticket pour obtenir de l'aide."}
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => openDialog()}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-xl"
                >
                  <FiPlus className="mr-2 h-5 w-5" />
                  Créer un ticket
                </motion.button>
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AnimatePresence>
          {showDeleteDialog && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed z-50 inset-0 overflow-y-auto"
              aria-labelledby="modal-title"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* ✅ CORRECTION : L'overlay n'a plus de blur, il est géré par le useEffect */}
                <div
                  className="fixed inset-0 modal-overlay z-40"
                  aria-hidden="true"
                  onClick={closeDeleteDialog}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0)',
                    transition: 'background-color 0.3s ease'
                  }}
                ></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <motion.div
                  initial={{ y: 20, opacity: 0, scale: 0.95 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={{ y: 20, opacity: 0, scale: 0.95 }}
                  className="inline-block align-bottom bg-gray-800 border border-amber-500/20 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative z-50"
                  onClick={handleModalClick}
                >
                  <div className="px-6 pt-6 pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-900/30 border border-red-500/30 sm:mx-0 sm:h-10 sm:w-10">
                        <FiAlertTriangle className="h-6 w-6 text-red-400" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 className="text-lg leading-6 font-semibold text-white" id="modal-title">
                          Supprimer le ticket
                        </h3>
                        <div className="mt-2">
                          <p className="text-sm text-gray-400">
                            Êtes-vous sûr de vouloir supprimer ce ticket ? Cette action est irréversible.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-gray-800/50 border-t border-amber-500/10 sm:px-6 sm:flex sm:flex-row-reverse">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-xl border border-transparent shadow-sm px-4 py-2.5 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/50 sm:ml-3 sm:w-auto"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Suppression..." : "Supprimer"}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-xl border border-amber-500/30 shadow-sm px-4 py-2.5 bg-amber-500/10 text-sm font-medium text-amber-400 hover:bg-amber-500/20 sm:mt-0 sm:w-auto"
                      onClick={closeDeleteDialog}
                      disabled={isDeleting}
                    >
                      Annuler
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create/Edit Form Modal */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed z-50 inset-0 overflow-y-auto"
              aria-labelledby="modal-title"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* ✅ CORRECTION : L'overlay n'a plus de blur, il est géré par le useEffect */}
                <div
                  className="fixed inset-0 modal-overlay z-40"
                  aria-hidden="true"
                  onClick={closeDialog}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0)',
                    transition: 'background-color 0.3s ease'
                  }}
                ></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <motion.div
                  initial={{ y: 20, opacity: 0, scale: 0.95 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={{ y: 20, opacity: 0, scale: 0.95 }}
                  className="inline-block align-bottom bg-gray-800 border border-amber-500/20 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full relative z-50"
                  onClick={handleModalClick}
                >
                  <div className="px-6 py-4 border-b border-amber-500/10 flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-white">
                      {editing ? "Modifier le ticket" : "Nouveau Ticket"}
                    </h3>
                    <button onClick={closeDialog} className="p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-white">
                      <FiXCircle size={20} />
                    </button>
                  </div>
                  <form onSubmit={handleSubmit} className="px-6 py-6">
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="type" className="block text-sm font-medium text-gray-300 mb-2">Type de ticket *</label>
<select 
                            id="type" 
                            name="type" 
                            value={currentTicket.type} 
                            onChange={handleInputChange} 
                            className="block w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-xl text-gray-200" 
                            required
                          >
                            <option value="reclamation">Réclamation</option>
                            <option value="remboursement">Demande de remboursement</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="reservation_id" className="block text-sm font-medium text-gray-300 mb-2">Lier à une réservation</label>
                          <select id="reservation_id" name="reservation_id" value={currentTicket.reservation_id || ""} onChange={handleInputChange} className="block w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-xl text-gray-200">
                            <option value="">Sélectionner (optionnel)</option>
                            {reservations.map((reservation) => (
                              <option key={reservation.id} value={reservation.id}>
                                Réservation #{reservation.id} - {new Date(reservation.created_at).toLocaleDateString()}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">Sujet *</label>
                        <input type="text" id="subject" name="subject" value={currentTicket.subject} onChange={handleInputChange} className="block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-gray-200" required maxLength={100} />
                        <p className="mt-1 text-xs text-gray-500">{currentTicket.subject.length}/100</p>
                      </div>
                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
                        <textarea id="description" name="description" rows={6} value={currentTicket.description} onChange={handleInputChange} className="block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-gray-200" required minLength={20} />
                        <p className="mt-1 text-xs text-gray-500">{currentTicket.description.length} caractères (min 20)</p>
                      </div>
                      {editing && (
                        <div>
                          <label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-2">Statut</label>
                          <select id="status" name="status" value={currentTicket.status} onChange={handleInputChange} className="block w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-xl text-gray-200">
                            <option value="nouveau">Nouveau</option>
                            <option value="en_cours">En cours</option>
                            <option value="resolu">Résolu</option>
                            <option value="ferme">Fermé</option>
                          </select>
                        </div>
                      )}
                    </div>
                    {error && <div className="mt-6 p-4 bg-red-900/20 text-red-300 text-sm rounded-xl">{error}</div>}
                    <div className="mt-8 flex justify-end items-center space-x-3">
                      <motion.button type="button" onClick={closeDialog} className="px-6 py-2.5 border border-gray-600 text-sm font-medium rounded-xl text-gray-300 hover:bg-gray-700">
                        Annuler
                      </motion.button>
                      <motion.button type="submit" disabled={isSubmitting} className="px-6 py-2.5 border border-transparent text-sm font-medium rounded-xl text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50">
                        {isSubmitting ? "Enregistrement..." : (editing ? "Mettre à jour" : "Créer le ticket")}
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Tickets